import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Stack,
  styled,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';

import { DateTimePicker, TextField } from '@components/base';
import FilterDefinition from '@models/FilterDefinition';
import useSearchAndFilter from '@modules/layouts/hooks/useSearchAndFilter';

import FilterDateExpressionHint from './FilterDateExpressionHint';

const Wrapper = styled('div')`
  flex: 1;
  display: flex;

  & > * {
    flex-grow: 1;
  }
`;

const timeUnits = ['minute', 'day', 'month'];

type FilterDateInputProps = {
  filterDefinition: FilterDefinition;
  setValue: (value: string) => void;
  value: string;
};

const InputModes = ['exp', 'date'] as const;
type InputMode = (typeof InputModes)[number];

const FilterDateInput: FC<FilterDateInputProps> = ({
  filterDefinition: { tokens },
  setValue,
  value,
}) => {
  const { t } = useTranslation();
  const { isReadonly } = useSearchAndFilter();

  const pattern = `(${tokens.join('|')})([\\(][\\-+][0-9]+(${timeUnits.join('|')})s?[\\)])?`;

  const [inputMode, setInputMode] = useState<InputMode>(
    !value || new RegExp(pattern).test(String(value)) ? 'exp' : 'date'
  );

  const handleSetDisplay = (d: InputMode) => {
    setValue('');
    setInputMode(d);
  };

  return (
    <Stack alignItems='flex-end' direction='row' flex={1} spacing={1}>
      <ToggleButtonGroup
        aria-label={t('dateInputMode.label')}
        disabled={isReadonly}
        exclusive
        onChange={(_, value) => handleSetDisplay(value as InputMode)}
        value={inputMode}
      >
        {InputModes.map(d => (
          <ToggleButton aria-label={t('dateInputMode.long', { context: d })} key={d} value={d}>
            <Tooltip title={t('dateInputMode.long', { context: d })}>
              <span>{t('dateInputMode.short', { context: d })}</span>
            </Tooltip>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Wrapper>
        {inputMode === 'exp' ? (
          <Autocomplete
            disabled={isReadonly}
            freeSolo
            fullWidth
            onChange={(_, val) => setValue(val || '')}
            options={tokens}
            renderInput={({
              InputLabelProps: _InputLabelProps,
              inputProps,
              InputProps,
              ...rest
            }) => (
              <TextField
                {...rest}
                label={t('value')}
                onChange={({ currentTarget: { value } }) => setValue(value)}
                placeholder='value'
                slotProps={{
                  input: isReadonly
                    ? InputProps
                    : { ...InputProps, startAdornment: <FilterDateExpressionHint /> },

                  htmlInput: { ...inputProps, pattern },
                }}
                value={value}
              />
            )}
            value={String(value) || null}
          />
        ) : (
          <DateTimePicker
            disabled={isReadonly}
            hideErrorMessage
            label={t('dateInputMode.short_date')}
            onChange={val => setValue(val || '')}
            value={value}
          />
        )}
      </Wrapper>
    </Stack>
  );
};

export default FilterDateInput;
